/**
 * M10.8: clients for the Admin Center (dashboard, civilizations, map, NPCs), the release center
 * (Admin → Frissítések) and the public changelog / version endpoints.
 */
import { apiFetch } from './client.ts';
import { critical } from './admin.ts';
import type { AdminAlert } from './admin.ts';

// ---- dashboard ----

export interface CenterDashboard {
  at: string;
  version: { app: string; gitSha: string | null; environment: string; release: string | null; deploymentActive: { version: string; status: string } | null };
  players: { registered: number; verified: number; new24h: number; admins: number; withoutCivilization: number; activeSessions: number; active15m: number; active24h: number } | null;
  world: { name: string; civilizations: number; npcCivilizations: number; systems: number; owned: number; free: number; ownedRatio: number; fleets: number; activeBattles: number; activeConflicts: number; startingCapacity: { eligible: number; preferred: number } } | null;
  system: {
    db: { ok: boolean; latencyMs: number };
    worker: { running: boolean; lastTickAt: string | null; lastTickMs: number; lastError: string | null; stale: boolean };
    events: { pending: number; processing: number; failed: number; failed24h: number; completed24h: number; overdue: number; oldestOverdueLagMs: number };
    websockets: number; memoryMb: { rss: number; heapUsed: number }; uptimeS: number; cpuPercent: number;
    latency: Array<{ route: string; p50: number; p99: number; count: number }>;
    eventLoopP99Ms: { p50: number; p99: number; max: number };
    dbPool: { total: number; idle: number; waiting: number };
  };
  backups: { lastVerifiedAt: string | null; lastOffsiteAt: string | null; lastRestoreTest: { ok: boolean; createdAt: string | null } | null } | null;
  alerts: AdminAlert[];
}
export const fetchDashboard = () => apiFetch<CenterDashboard>('/admin/center/dashboard');

// ---- civilizations ----

export interface CivRow {
  id: string; name: string; color: string; emblem: string; owner: string; isNpc: boolean; personality: string | null;
  systems: number; fleets: number; ships: number; metal: number; energy: number; credits: number; lastSeenAt: string | null; createdAt: string; home: string;
}
export interface CivDetail {
  id: string; name: string; color: string; createdAt: string | null; home: string;
  owner: { id: string; username: string; isNpc: boolean; role: string; lastSeenAt: string | null; emailVerified: boolean };
  resources: { metal: number; energy: number; credits: number; lastProductionAt: string | null } | null;
  systems: Array<{ id: string; name: string; starType: string; acquiredAt: string | null }>;
  fleets: Array<{ id: string; name: string; status: string; role: string; system: string; ships: number }>;
  diplomacy: Array<{ other: string; status: string }>;
  recentBattles: Array<{ id: string; status: string; winner: string | null; startedAt: string | null; system: string; role: string; opponent: string }>;
}
export const fetchCivs = (q: string, kind: string) => apiFetch<{ civilizations: CivRow[] }>(`/admin/center/civilizations?kind=${encodeURIComponent(kind)}${q ? `&q=${encodeURIComponent(q)}` : ''}`);
export const fetchCivDetail = (id: string) => apiFetch<CivDetail>(`/admin/center/civilizations/${id}`);

// ---- map ----

export interface MapSystem {
  id: string; name: string; x: number; y: number; starType: string; habitability: number; richness: number; planets: number;
  owner: { id: string; name: string; color: string; isNpc: boolean } | null; home: boolean; startEligible: boolean;
  fleets: Array<{ civId: string; civName: string; fleets: number; ships: number }>;
  conflict: { status: string; attacker: string; defender: string; endsAt: string | null } | null;
}
export interface AdminMapData {
  world?: { id: string; name: string }; systems: MapSystem[]; lanes: Array<{ a: string; b: string }>;
  totals: { systems: number; owned: number; startEligible: number; conflicts: number; fleets: number };
}
export const fetchAdminMap = () => apiFetch<AdminMapData>('/admin/center/map');

// ---- NPCs ----

export interface NpcRow {
  civId: string; name: string; color: string; personality: string; turnCount: number; nextTurnAt: string; overdueMs: number;
  economy: { metal: number; energy: number; credits: number; systems: number; fleets: number; ships: number };
  goal: string; recent: Array<{ kind: string; ok: boolean; detail?: string }>; failedRecent: number;
  cooldowns: Array<{ action: string; failures: number; reason: string; until: string }>;
  stuck: { stuck: boolean; reasons: string[] };
}
export interface NpcOverview {
  npcs: NpcRow[]; summary: { total: number; stuck: number; failedActionsRecent: number; overdue: number };
  enabled: boolean; turnIntervalMs: number; decisionTimeMs?: { p50: number; p99: number; count: number }; turnsSinceStart?: number;
}
export const fetchNpcs = () => apiFetch<NpcOverview>('/admin/center/npcs');
export const resetNpcCooldowns = (civId: string, reason: string, ask: () => Promise<string | null>) =>
  critical<{ cleared: number }>(`/admin/center/npcs/${civId}/reset-cooldowns`, { reason }, ask);

// ---- releases ----

export type Lifecycle = 'development' | 'ready' | 'installing' | 'live' | 'live_unregistered' | 'failed' | 'rolled_back' | 'superseded' | 'unavailable';

export interface ReleaseView {
  id: string; version: string; channel: 'stable' | 'rc'; gitSha: string; shortSha: string; builtAt: string; imageDigest: string; ciStatus: string; ciRunUrl: string | null;
  title: string; summary: string; notes: ReleaseNotes; migrations: { latest: number; additiveAll: boolean; files: number };
  installable: boolean; notInstallableReason: string | null; withdrawn: boolean; published: boolean;
  state: 'current' | 'available' | 'older' | 'unavailable';
  lifecycle: Lifecycle; releasedAt: string | null;
  lastDeployment: { id: string; environment: string; status: string; finishedAt: string | null } | null;
}
export interface DeploymentView {
  id: string; kind: 'deploy' | 'rollback'; environment: string; version: string; imageDigest: string; status: string;
  requestedAt: string; startedAt: string | null; finishedAt: string | null; error: string | null; runUrl: string | null;
  healthcheck: { ok?: boolean } | null; preflightOk: boolean | null; detail: Record<string, unknown>;
}
export interface UpdatesOverview {
  environment: 'staging' | 'production';
  running: { version: string; gitSha: string | null; release: { id: string; version: string } | null };
  releases: ReleaseView[]; deployments: DeploymentView[]; active: { id: string; version: string; status: string } | null;
  github: { configured: boolean; repository: string | null };
  secondFactor: { required: boolean; enrolled: boolean }; confirmPhrase: string; publishNotesOn: string;
  bootstrap: BootstrapStatus;
}

export interface BootstrapStatus {
  available: boolean; reasons: string[]; environment: string; phrase: string;
  running: { version: string; gitSha: string | null; imageDigest: string | null };
  candidate: { releaseId: string; version: string } | null;
  done: { version: string; performedAt: string; gitSha: string; imageDigest: string } | null;
}
export interface PreflightCheck { id: string; label: string; ok: boolean; blocking: boolean; detail: string }
export interface Preflight {
  ok: boolean; checks: PreflightCheck[]; kind: 'deploy' | 'rollback'; from: string | null; to: string; environment: string;
  rollback: { mode: 'automatic' | 'blocked' | 'noop'; blockers: Array<{ migration: string; reasons: string[] }> } | null; manualSteps: string[];
}
export const fetchUpdates = () => apiFetch<UpdatesOverview>('/admin/updates');
export const syncReleases = () => apiFetch<{ fetched: number; added: string[]; skipped: Array<{ tag: string; reason: string }> }>('/admin/updates/sync', { method: 'POST', body: '{}' });
export const runPreflight = (releaseId: string, kind: 'deploy' | 'rollback') =>
  apiFetch<Preflight>('/admin/updates/preflight', { method: 'POST', body: JSON.stringify({ releaseId, kind }) });
export const fetchDeployment = (id: string) => apiFetch<DeploymentView & { preflight: unknown }>(`/admin/updates/deployments/${id}`);
export const startDeployment = (body: { releaseId: string; kind: 'deploy' | 'rollback'; requestId: string; confirm: string; reason: string; totp?: string }, ask: () => Promise<string | null>) =>
  critical<{ deploymentId: string; status: string; duplicate: boolean }>('/admin/updates/deploy', body, ask);
export const bootstrapFirstRelease = (body: { version: string; confirm: string; reason: string; totp: string }, ask: () => Promise<string | null>) =>
  critical<{ duplicate: boolean; version: string; performedAt: string; published: boolean }>('/admin/updates/bootstrap', body, ask);
export const cancelDeployment = (id: string, ask: () => Promise<string | null>) => critical<{ status: string }>(`/admin/updates/deployments/${id}/cancel`, {}, ask);

// ---- public changelog / version ----

export interface ReleaseNotes {
  version: string; title: string; summary: string; minClientVersion: string | null;
  /** Verified time of the successful installation (public changelog only). */
  releasedAt?: string;
  sections: { features: string[]; improvements: string[]; fixes: string[]; important: string[] };
}
export const fetchChangelog = () => apiFetch<{ current: string; releases: ReleaseNotes[] }>('/public/changelog', { auth: false });
export const fetchPublicVersion = () => apiFetch<{ version: string; minClientVersion: string | null }>('/public/version', { auth: false });

/** Version of this client build (set at image build time); "dev" for local development. */
export const CLIENT_VERSION = String(import.meta.env['VITE_APP_VERSION'] ?? 'dev');

export function compareVersions(a: string, b: string): number {
  const p = (v: string) => { const m = /^(\d+)\.(\d+)\.(\d+)(?:-rc\.(\d+))?$/.exec(v); return m ? [+m[1]!, +m[2]!, +m[3]!, m[4] === undefined ? Infinity : +m[4]] : null; };
  const x = p(a), y = p(b);
  if (!x || !y) return 0;
  for (let i = 0; i < 4; i++) if (x[i] !== y[i]) return x[i]! < y[i]! ? -1 : 1;
  return 0;
}
